//
// Help → Tutorials popup. One collapsible group per page; only the page the
// user is currently on is expanded by default. Each section is a single
// clickable line (whole row plays it): a green check when done. A section that
// needs a selection that is not on screen is dimmed with
// a tooltip explaining what to do first. Rendered through a portal so the
// menu's overflow-hidden does not clip it. Only the list scrolls, so the
// release notes and the auto-explain switch stay reachable.
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  useTownAssets,
  useTutorial,
} from '../stores';
import appTooltipProps from '../utils/appTooltipProps';
import config from '../utils/config';

import SideMenuButtonPopup from './SideMenuButtonPopup';
import SvgColorImg from './SvgColorImg';

// Titles each group like its side-menu entry (generic menu label keys).
const pageLabelKey = {
  home: 'dashboard',
  cases: 'reports',
  tasks: 'tasks',
  qrs: 'placesEquipment',
  polls: 'polls',
  comm: 'publications',
  responders: 'responders',
  users: 'users',
  partners: 'partnerships',
  settings: 'settings',
  profile: 'profile',
};

const isVisible = (selector) => {
  if (!selector) return false;
  const element = document.querySelector(selector);
  return Boolean(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
};

function TutorialsFlyout({ anchorRect, onClose }) {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { tutorial } = useTutorial();
  const {
    sections,
    replay,
    reset,
    skip,
    setSkip,
    hasReleaseNotes,
    playReleaseNotes,
  } = tutorial;

  const location = useLocation();
  const currentPage = location.pathname.split('/')[1] || 'home';
  const currentSub = location.pathname.split('/')[2];

  // One group per side-menu page, split further per sub-page when a page has
  // them (e.g. QR codes → list / templates / printing). The group key is what
  // the collapse toggle and the default-expansion test key off.
  const groupKeyOf = (section) => (section.subPageKey
    ? `${section.pageKey}/${section.subPageKey}`
    : section.pageKey);

  const groups = new Map();
  sections.forEach((section) => {
    const key = groupKeyOf(section);
    if (!groups.has(key)) {
      groups.set(
        key,
        {
          pageKey: section.pageKey,
          subPageKey: section.subPageKey,
          subLabelKey: section.subLabelKey,
          subLabelType: section.subLabelType,
          sections: [],
        },
      );
    }
    groups.get(key).sections.push(section);
  });

  // The one group matching what is on screen: the sub-page's when there is one,
  // else the page's own, which would otherwise be current on every sub-page too.
  const onCurrentPage = (group) => (group.pageKey === currentPage);
  const currentGroupKey = (
    [...groups.entries()].find(([, group]) => onCurrentPage(group) && (group.subPageKey === currentSub))
    ?? [...groups.entries()].find(([, group]) => onCurrentPage(group) && (!group.subPageKey))
  )?.[0];

  const [expanded, setExpanded] = useState(() => new Set(currentGroupKey ? [currentGroupKey] : []));

  // Scroll the list to the group of the page on screen when the flyout opens.
  const currentGroupRef = useRef(null);
  useEffect(
    () => {
      currentGroupRef.current?.scrollIntoView({ block: 'nearest' });
    },
    [],
  );

  const toggleGroup = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const groupTitle = (group) => {
    const pageLabel = translate(pageLabelKey[group.pageKey] ?? 'tutorials');
    return group.subLabelKey
      ? `${pageLabel} - ${translate(group.subLabelKey, group.subLabelType)}`
      : pageLabel;
  };

  const playSection = (section) => {
    onClose();
    replay(section.id);
  };

  const footer = (
    <>
      <label className="flex items-center gap-2 text-sm cursor-pointer border-t border-pxMainOutline mt-2 pt-2 px-1">
        <input
          type="checkbox"
          checked={!skip}
          onChange={(event) => {
            setSkip(!event.target.checked);
            onClose();
          }}
        />
        <span className="whitespace-nowrap">{translate('autoExplain', 'tutorial')}</span>
      </label>

      {(config.dev || config.demo)
        ? (
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="w-full text-left text-sm text-pxUiAlert border-0 bg-transparent cursor-pointer rounded px-1 py-1.5 mt-1 hover:bg-pxBgSubtle"
            >
              Reset tutorials and explanations (dev/demo)
            </button>
          )
        : null}
    </>
  );

  return (
    <SideMenuButtonPopup
      anchorRect={anchorRect}
      onClose={onClose}
      keepOpenSelector="[data-help-anchor]"
      title={translate('tutorials')}
      footer={footer}
    >
      {hasReleaseNotes
        ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                playReleaseNotes(true);
              }}
              className="w-full flex items-center gap-3 px-1 py-1.5 rounded text-left border-0 bg-transparent cursor-pointer hover:bg-pxBgSubtle"
            >
              <SvgColorImg
                src="/images/svg/gui/new2.svg"
                description="."
                imgColorAsCssFill
                className="w-4 h-4 shrink-0 fill-pxPrimary"
              />
              <span className="flex-1 whitespace-nowrap">{translate('whatsNewTitle', 'tutorial')}</span>
            </button>
          )
        : null}

      {[...groups.entries()].map(([key, group]) => {
        const open = expanded.has(key);
        return (
          <div key={key} ref={(key === currentGroupKey) ? currentGroupRef : undefined} className="pt-2">
            <button
              type="button"
              onClick={() => toggleGroup(key)}
              className="flex items-center justify-start gap-1.5 px-1 pb-1 bg-transparent border-0 cursor-pointer"
            >
              <span className="text-sm font-semibold opacity-70 whitespace-nowrap">
                {groupTitle(group)}
              </span>
              <SvgColorImg
                src="/images/svg/gui/arrowDown.svg"
                description="."
                imgColorAsCssFill
                className={classNames(
                  'w-3.5 h-3.5 shrink-0 fill-pxTxt opacity-60 transition-transform',
                  open ? 'rotate-none' : '-rotate-90',
                )}
              />
            </button>

            {open
              ? group.sections.map((section) => {
                  const canPlay = (!section.needsSelection) || isVisible(section.trigger);
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={canPlay ? () => playSection(section) : undefined}
                      {...appTooltipProps(canPlay ? undefined : translate(section.playHint, 'tutorial'))}
                      className={classNames(
                        'w-full flex items-center gap-3 pl-3 pr-2 py-1.5 rounded text-left border-0 bg-transparent',
                        canPlay ? 'cursor-pointer hover:bg-pxBgSubtle' : 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                        {section.status === 'done'
                          ? (
                              <SvgColorImg
                                src="/images/svg/gui/check.svg"
                                imgColor="#16a34a"
                                description="."
                                className="w-4 h-4"
                              />
                            )
                          : null}
                      </span>
                      <span className="flex-1 whitespace-nowrap min-w-0">{translate(section.labelKey, 'tutorial')}</span>

                      <SvgColorImg
                        src="/images/svg/gui/play.svg"
                        description="."
                        imgColorAsCssFill
                        className="w-5 h-5 shrink-0 fill-pxPrimary"
                      />
                    </button>
                  );
                })
              : null}
          </div>
        );
      })}

      {(!sections.length)
        ? <p className="italic opacity-70 px-1 py-2">{translate('noTutorials', 'tutorial')}</p>
        : null}
    </SideMenuButtonPopup>
  );
}

TutorialsFlyout.propTypes = {
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TutorialsFlyout;
